import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  SCHOOL_HOUSE_TEAMS, 
  DEFAULT_PHYSICAL_SPORTS,
  getAllSports,
  getCustomSportsLibrary,
  saveCustomSportsLibrary,
  getHouseScores, 
  saveHouseScores, 
  getSportsEvents, 
  saveSportsEvents, 
  getHouseMedals, 
  saveHouseMedals,
  getCustomHouses,
  saveCustomHouses,
  getActiveSportEvent,
  saveActiveSportEvent,
  getOrganizerName,
  saveOrganizerName
} from '../utils/quizData';
import { playWinFanfare, playTickSound, playCorrectChime } from '../utils/soundEffects';

const { 
  FiAward, FiPlus, FiTrash2, FiExternalLink, FiDownload, 
  FiRefreshCw, FiEdit3, FiCheck, FiX, FiActivity, FiUsers, 
  FiFilter, FiSearch, FiTv, FiMaximize2, FiSliders, FiHelpCircle,
  FiBookOpen, FiShare2, FiRadio, FiShield, FiSave
} = FiIcons;

const CATEGORIES = [
  'All Games',
  'Indoor Fun (<7 Years)',
  'Track & Athletics',
  'Field & Team Games',
  'Fun & Carnival Races',
  'Drill & Team Spirit',
  'Custom Added Sports'
];

function SchoolSportsBarometerManagerComponent() {
  const [houseScores, setHouseScores] = useState(getHouseScores());
  const [houseMedals, setHouseMedals] = useState(getHouseMedals());
  const [sportsEvents, setSportsEvents] = useState(getSportsEvents());
  const [houses, setHouses] = useState(getCustomHouses());
  const [organizerName, setOrganizerName] = useState(getOrganizerName());
  const [activeSportEvent, setActiveSportEvent] = useState(getActiveSportEvent());
  const [allSportsList, setAllSportsList] = useState(getAllSports());

  // Organizer Edit State
  const [isEditingOrganizer, setIsEditingOrganizer] = useState(false);
  const [organizerInput, setOrganizerInput] = useState(organizerName);

  // Form State for Logging a Match
  const [selectedCategory, setSelectedCategory] = useState('All Games');
  const [selectedSport, setSelectedSport] = useState(allSportsList[0]?.name || '100m Dash Sprint');
  const [customSportInput, setCustomSportInput] = useState('');
  const [saveCustomToLibrary, setSaveCustomToLibrary] = useState(true);
  const [selectedDivision, setSelectedDivision] = useState('Junior (Grades 5-8)');
  const [selectedPlace, setSelectedPlace] = useState('1st'); // '1st' | '2nd' | '3rd' | 'participation'
  const [selectedHouse, setSelectedHouse] = useState('red');
  const [studentName, setStudentName] = useState('');
  const [classRoll, setClassRoll] = useState('');
  const [pointsInput, setPointsInput] = useState(50);
  const [eventNotes, setEventNotes] = useState('');

  // Rules Modal State
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
  const [rulesSportData, setRulesSportData] = useState(null);

  // Create Custom Sport Modal State
  const [isCreateSportOpen, setIsCreateSportOpen] = useState(false);
  const [newSportForm, setNewSportForm] = useState({
    name: '',
    category: 'Indoor Fun (<7 Years)',
    icon: '🎈',
    recommendedAge: 'Ages 3-6 / Under 7',
    rules: '',
    objective: ''
  });

  // Table Filter State
  const [filterHouse, setFilterHouse] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const baseUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : '';

  useEffect(() => {
    const handleUpdate = () => {
      setHouseScores(getHouseScores());
      setHouseMedals(getHouseMedals());
      setSportsEvents(getSportsEvents());
      setHouses(getCustomHouses());
      setOrganizerName(getOrganizerName());
      setActiveSportEvent(getActiveSportEvent());
      setAllSportsList(getAllSports());
    };
    window.addEventListener('houseScoresUpdated', handleUpdate);
    window.addEventListener('sportsEventsUpdated', handleUpdate);
    window.addEventListener('houseMedalsUpdated', handleUpdate);
    window.addEventListener('customHousesUpdated', handleUpdate);
    window.addEventListener('organizerNameUpdated', handleUpdate);
    window.addEventListener('activeSportEventUpdated', handleUpdate);
    window.addEventListener('sportsLibraryUpdated', handleUpdate);
    window.addEventListener('storage', handleUpdate);
    return () => {
      window.removeEventListener('houseScoresUpdated', handleUpdate);
      window.removeEventListener('sportsEventsUpdated', handleUpdate);
      window.removeEventListener('houseMedalsUpdated', handleUpdate);
      window.removeEventListener('customHousesUpdated', handleUpdate);
      window.removeEventListener('organizerNameUpdated', handleUpdate);
      window.removeEventListener('activeSportEventUpdated', handleUpdate);
      window.removeEventListener('sportsLibraryUpdated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, []);

  // Update default points when place changes
  const handlePlaceChange = (place) => {
    setSelectedPlace(place);
    if (place === '1st') setPointsInput(50);
    else if (place === '2nd') setPointsInput(30);
    else if (place === '3rd') setPointsInput(15);
    else setPointsInput(5);
  };

  // Find sport object for currently selected sport
  const currentSportObject = allSportsList.find(s => s.name === selectedSport) || {
    name: customSportInput || selectedSport,
    category: 'Custom Event',
    icon: '🏆',
    rules: ['Compete fairly according to referee instructions.', 'Maintain great sportsmanship!'],
    objective: 'Score the highest points for your house team'
  };

  // Broadcast Event to Projector
  const handleBroadcastEvent = (sportObj = currentSportObject, showRules = false) => {
    const finalTitle = customSportInput.trim() ? customSportInput.trim() : sportObj.name;
    const broadcastData = {
      sportName: finalTitle,
      category: sportObj.category || 'General Sports',
      icon: sportObj.icon || '🏆',
      division: selectedDivision,
      recommendedAge: sportObj.recommendedAge || selectedDivision,
      rules: sportObj.rules || [
        'Compete fairly according to official referee instructions.',
        'Support your teammates and celebrate good sportsmanship!'
      ],
      objective: sportObj.objective || 'Earn the highest points for your house team',
      pointsScheme: { first: 50, second: 30, third: 15, participation: 5 },
      showRulesOnProjector: showRules,
      timestamp: Date.now()
    };

    saveActiveSportEvent(broadcastData);
    setActiveSportEvent(broadcastData);
    playTickSound(880, 0.05);
    alert(`📡 Broadcast Updated: "${finalTitle}" is now featured live on the Projector View!`);
  };

  const handleToggleProjectorRules = () => {
    if (!activeSportEvent) {
      handleBroadcastEvent(currentSportObject, true);
      return;
    }
    const updated = {
      ...activeSportEvent,
      showRulesOnProjector: !activeSportEvent.showRulesOnProjector
    };
    saveActiveSportEvent(updated);
    setActiveSportEvent(updated);
    playTickSound(750, 0.04);
  };

  const handleClearBroadcast = () => {
    saveActiveSportEvent(null);
    setActiveSportEvent(null);
    playTickSound(440, 0.04);
  };

  // Save School / Organizer Name
  const handleSaveOrganizer = () => {
    const clean = organizerInput.trim();
    if (!clean) return;
    saveOrganizerName(clean);
    setOrganizerName(clean);
    setIsEditingOrganizer(false);
    playCorrectChime();
  };

  // Create & Save New Custom Sport
  const handleCreateNewSport = (e) => {
    e?.preventDefault();
    if (!newSportForm.name.trim()) return alert("Sport name is required.");

    const rulesArray = newSportForm.rules
      ? newSportForm.rules.split('\n').map(r => r.trim()).filter(Boolean)
      : ['Follow referee guidance and play with great sportsmanship!'];

    const newSportItem = {
      id: `custom_sport_${Date.now()}`,
      name: newSportForm.name.trim(),
      category: newSportForm.category,
      icon: newSportForm.icon || '⭐',
      recommendedAge: newSportForm.recommendedAge || 'All Ages',
      rules: rulesArray,
      objective: newSportForm.objective.trim() || 'Score podium points for your house'
    };

    const currentCustom = getCustomSportsLibrary();
    const updatedCustom = [...currentCustom, newSportItem];
    saveCustomSportsLibrary(updatedCustom);
    setAllSportsList(getAllSports());

    setSelectedSport(newSportItem.name);
    setCustomSportInput('');
    setIsCreateSportOpen(false);

    // Auto broadcast to projector
    handleBroadcastEvent(newSportItem);

    // Reset form
    setNewSportForm({
      name: '',
      category: 'Indoor Fun (<7 Years)',
      icon: '🎈',
      recommendedAge: 'Ages 3-6 / Under 7',
      rules: '',
      objective: ''
    });

    playCorrectChime();
    alert(`🎉 Custom sport "${newSportItem.name}" has been saved to your library and will show on the projector!`);
  };

  // 1. Submit Physical Sports Score
  const handleAwardSport = (e) => {
    e?.preventDefault();
    let sportTitle = customSportInput.trim() ? customSportInput.trim() : selectedSport;
    if (!sportTitle) return alert("Please specify the sport name.");

    // If custom sport input was entered and save to library is checked, save it!
    if (customSportInput.trim() && saveCustomToLibrary) {
      const currentCustom = getCustomSportsLibrary();
      if (!currentCustom.some(s => s.name.toLowerCase() === customSportInput.trim().toLowerCase())) {
        const autoSavedSport = {
          id: `custom_sport_${Date.now()}`,
          name: customSportInput.trim(),
          category: 'Custom Event',
          icon: '⭐',
          recommendedAge: selectedDivision,
          rules: ['Follow official event rules and play fairly!'],
          objective: 'Score podium points for your house'
        };
        saveCustomSportsLibrary([...currentCustom, autoSavedSport]);
        setAllSportsList(getAllSports());
      }
    }

    const awardedPts = Number(pointsInput) || 0;
    const targetHouse = houses.find(h => h.id === selectedHouse) || houses[0];

    const newEvent = {
      id: `sport_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      sportName: sportTitle,
      division: selectedDivision,
      place: selectedPlace,
      points: awardedPts,
      houseId: selectedHouse,
      houseName: targetHouse.name,
      studentName: studentName.trim() || 'House Team',
      classRoll: classRoll.trim(),
      notes: eventNotes.trim(),
      timestamp: new Date().toISOString()
    };

    // Update Scores
    const updatedScores = {
      ...houseScores,
      [selectedHouse]: (houseScores[selectedHouse] || 0) + awardedPts
    };
    setHouseScores(updatedScores);
    saveHouseScores(updatedScores);

    // Update Medals
    const updatedMedals = { ...houseMedals };
    if (!updatedMedals[selectedHouse]) {
      updatedMedals[selectedHouse] = { gold: 0, silver: 0, bronze: 0 };
    }
    if (selectedPlace === '1st') updatedMedals[selectedHouse].gold += 1;
    else if (selectedPlace === '2nd') updatedMedals[selectedHouse].silver += 1;
    else if (selectedPlace === '3rd') updatedMedals[selectedHouse].bronze += 1;
    setHouseMedals(updatedMedals);
    saveHouseMedals(updatedMedals);

    // Update Event Log
    const updatedEvents = [newEvent, ...sportsEvents];
    setSportsEvents(updatedEvents);
    saveSportsEvents(updatedEvents);

    // Also auto-update Active Sport on Projector so projector announces it!
    handleBroadcastEvent({
      name: sportTitle,
      category: currentSportObject.category || 'General Sports',
      icon: currentSportObject.icon || '🏆',
      rules: currentSportObject.rules || ['Play fair and have fun!'],
      objective: currentSportObject.objective || 'Win podium medals for your house'
    });

    // Audio & Feedback
    if (selectedPlace === '1st') {
      playWinFanfare();
    } else {
      playCorrectChime();
    }

    // Reset Form fields
    setStudentName('');
    setClassRoll('');
    setEventNotes('');
    setCustomSportInput('');
  };

  // 2. Quick Point Bumper
  const handleScoreBump = (houseId, delta) => {
    const cur = houseScores[houseId] || 0;
    const next = Math.max(0, cur + delta);
    const updated = { ...houseScores, [houseId]: next };
    setHouseScores(updated);
    saveHouseScores(updated);
    playTickSound(delta > 0 ? 880 : 440, 0.05);
  };

  // 3. Delete / Rollback an Event
  const handleDeleteEvent = (event) => {
    if (!window.confirm(`Delete "${event.sportName}" awarded to ${event.houseName}? Points and medals will be rolled back.`)) {
      return;
    }

    // Rollback score
    const updatedScores = {
      ...houseScores,
      [event.houseId]: Math.max(0, (houseScores[event.houseId] || 0) - event.points)
    };
    setHouseScores(updatedScores);
    saveHouseScores(updatedScores);

    // Rollback medal
    const updatedMedals = { ...houseMedals };
    if (updatedMedals[event.houseId]) {
      if (event.place === '1st') updatedMedals[event.houseId].gold = Math.max(0, updatedMedals[event.houseId].gold - 1);
      if (event.place === '2nd') updatedMedals[event.houseId].silver = Math.max(0, updatedMedals[event.houseId].silver - 1);
      if (event.place === '3rd') updatedMedals[event.houseId].bronze = Math.max(0, updatedMedals[event.houseId].bronze - 1);
      setHouseMedals(updatedMedals);
      saveHouseMedals(updatedMedals);
    }

    const filtered = sportsEvents.filter(e => e.id !== event.id);
    setSportsEvents(filtered);
    saveSportsEvents(filtered);
    playTickSound(500, 0.05);
  };

  // 4. Reset All Scores & Medals
  const handleResetAll = () => {
    if (window.confirm("Are you sure you want to reset all house scores, medals, and sports history? This cannot be undone.")) {
      const resetScores = { red: 0, blue: 0, green: 0, yellow: 0 };
      const resetMedals = {
        red: { gold: 0, silver: 0, bronze: 0 },
        blue: { gold: 0, silver: 0, bronze: 0 },
        green: { gold: 0, silver: 0, bronze: 0 },
        yellow: { gold: 0, silver: 0, bronze: 0 }
      };
      setHouseScores(resetScores);
      saveHouseScores(resetScores);
      setHouseMedals(resetMedals);
      saveHouseMedals(resetMedals);
      setSportsEvents([]);
      saveSportsEvents([]);
      saveActiveSportEvent(null);
      setActiveSportEvent(null);
      playTickSound(400, 0.08);
    }
  };

  // 5. Export Sports Day Standings to Excel & CSV
  const handleExportExcel = () => {
    try {
      const standingsRows = sortedHouses.map((h, i) => ({
        "Championship Rank": `#${i + 1}`,
        "House Name": h.name,
        "Color / House": h.house,
        "Total Championship Points": houseScores[h.id] || 0,
        "Gold Medals (1st)": houseMedals[h.id]?.gold || 0,
        "Silver Medals (2nd)": houseMedals[h.id]?.silver || 0,
        "Bronze Medals (3rd)": houseMedals[h.id]?.bronze || 0
      }));

      const logRows = sportsEvents.map((ev, i) => ({
        "Event ID": i + 1,
        "Sport / Athletic Event": ev.sportName,
        "Division": ev.division,
        "Position": ev.place,
        "House Awarded": ev.houseName,
        "Points Awarded": ev.points,
        "Student Athlete": ev.studentName,
        "Class / Roll": ev.classRoll || '',
        "Notes": ev.notes || '',
        "Date & Time": new Date(ev.timestamp).toLocaleString()
      }));

      const wb = XLSX.utils.book_new();
      const wsStandings = XLSX.utils.json_to_sheet(standingsRows);
      const wsLog = XLSX.utils.json_to_sheet(logRows);

      XLSX.utils.book_append_sheet(wb, wsStandings, "Championship Standings");
      XLSX.utils.book_append_sheet(wb, wsLog, "Sports Match History");
      XLSX.writeFile(wb, `sports_day_championship_${new Date().toISOString().split('T')[0]}.xlsx`);
      playTickSound(880, 0.05);
    } catch (e) {
      console.error("Export error:", e);
      alert("Failed to export Excel report: " + e.message);
    }
  };

  const safeScores = (houseScores && typeof houseScores === 'object' && !Array.isArray(houseScores)) ? houseScores : { red: 0, blue: 0, green: 0, yellow: 0 };
  const safeHouses = (Array.isArray(houses) && houses.length > 0) ? houses : SCHOOL_HOUSE_TEAMS;
  const safeMedals = (houseMedals && typeof houseMedals === 'object' && !Array.isArray(houseMedals)) ? houseMedals : {};
  const safeEvents = Array.isArray(sportsEvents) ? sportsEvents : [];
  const safeSportsList = (Array.isArray(allSportsList) && allSportsList.length > 0) ? allSportsList : DEFAULT_PHYSICAL_SPORTS;

  // Filter sports list by category tab
  const filteredSportsList = safeSportsList.filter(s => {
    if (!s) return false;
    if (selectedCategory === 'All Games') return true;
    if (selectedCategory === 'Custom Added Sports') {
      const customIds = getCustomSportsLibrary().map(c => c?.id);
      return customIds.includes(s.id);
    }
    return s.category === selectedCategory;
  });

  // Sort houses by score
  const sortedHouses = [...safeHouses].sort((a, b) => (safeScores[b?.id] || 0) - (safeScores[a?.id] || 0));
  const maxScore = Math.max(...Object.values(safeScores).map(v => Number(v) || 0), 100);

  // Filtered Events
  const filteredEvents = safeEvents.filter(ev => {
    if (!ev) return false;
    const matchHouse = filterHouse === 'all' || ev.houseId === filterHouse;
    const sName = String(ev.sportName || '').toLowerCase();
    const stName = String(ev.studentName || '').toLowerCase();
    const hName = String(ev.houseName || '').toLowerCase();
    const sTerm = String(searchTerm || '').toLowerCase();
    const matchSearch = sName.includes(sTerm) || stName.includes(sTerm) || hName.includes(sTerm);
    return matchHouse && matchSearch;
  });

  return (
    <div className="space-y-6">

      {/* TOP BANNER WITH SCHOOL / ORGANIZER NAME EDIT & ARENA LAUNCH */}
      <div className="bg-gradient-to-r from-amber-500/15 via-indigo-500/15 to-purple-500/15 border-2 border-amber-500/30 rounded-2xl p-5 md:p-6 shadow-sm space-y-4">
        
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-800 dark:text-amber-300 text-xs font-black uppercase flex items-center gap-1.5 shadow-sm">
                <SafeIcon icon={FiShield} /> Official School / Event Organizer Branding
              </span>
              <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-0.5 rounded-full border border-emerald-300 dark:border-emerald-800">
                Live on Projector Screen
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              <span>🏫 {organizerName}</span>
            </h1>

            <p className="text-xs text-slate-600 dark:text-slate-300 max-w-xl">
              This name is projected in giant letters across the auditorium on the House Barometer stage.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href={`${baseUrl}#/house-barometer`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-950 font-black text-xs shadow-lg shadow-amber-500/20 transition-all flex items-center gap-2"
            >
              <SafeIcon icon={FiTv} className="text-base" />
              <span>Launch Projector Screen</span>
              <SafeIcon icon={FiExternalLink} />
            </a>

            <button
              type="button"
              onClick={handleExportExcel}
              className="px-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 font-bold text-xs transition-colors flex items-center gap-1.5 shadow-sm"
            >
              <SafeIcon icon={FiDownload} />
              <span>Export Scoresheet (.xlsx)</span>
            </button>
          </div>
        </div>

        {/* Quick Edit Input for School / Event Organizer Name */}
        <div className="pt-3 border-t border-amber-500/20 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
          <label className="text-xs font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap flex items-center gap-1.5">
            <SafeIcon icon={FiEdit3} className="text-indigo-500" />
            <span>Change School / Organizer Name:</span>
          </label>
          <div className="flex-1 flex items-center gap-2">
            <input
              type="text"
              value={organizerInput}
              onChange={e => setOrganizerInput(e.target.value)}
              placeholder="e.g. Delhi Public School Annual Sports Championship"
              className="flex-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 focus:border-indigo-500 rounded-xl px-3.5 py-2 text-sm font-bold text-slate-900 dark:text-white outline-none shadow-sm"
            />
            <button
              type="button"
              onClick={handleSaveOrganizer}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black shadow-md transition-all active:scale-95 whitespace-nowrap flex items-center gap-1.5"
            >
              <SafeIcon icon={FiSave} />
              <span>Save & Update Big Screen</span>
            </button>
          </div>
        </div>

      </div>

      {/* ACTIVE EVENT BROADCASTER CARD (WHAT IS SHOWN ON PROJECTOR) */}
      <div className="bg-slate-900 text-white border border-indigo-500/40 rounded-2xl p-4 sm:p-5 shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-full bg-gradient-to-l from-indigo-500/20 to-transparent pointer-events-none"></div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/30 border border-indigo-400/40 flex items-center justify-center text-2xl shrink-0">
              {activeSportEvent?.icon || '📡'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping"></span>
                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400">
                  {activeSportEvent ? 'LIVE ON AUDITORIUM PROJECTOR' : 'NO EVENT BROADCASTING'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-black text-white flex items-center gap-2 mt-0.5">
                <span>{activeSportEvent?.sportName || 'Select a game below & click "Broadcast Event"'}</span>
                {activeSportEvent?.division && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-slate-300 font-normal">
                    {activeSportEvent.division}
                  </span>
                )}
              </h3>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => handleBroadcastEvent(currentSportObject, false)}
              className="px-3.5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black transition-colors flex items-center gap-1.5 shadow-md"
              title="Broadcast currently selected game to the projector screen"
            >
              <SafeIcon icon={FiRadio} />
              <span>Broadcast "{customSportInput.trim() || selectedSport}"</span>
            </button>

            <button
              type="button"
              onClick={handleToggleProjectorRules}
              className={`px-3.5 py-2 rounded-xl text-xs font-black transition-colors flex items-center gap-1.5 ${
                activeSportEvent?.showRulesOnProjector
                  ? 'bg-amber-400 text-slate-950 shadow-md ring-2 ring-amber-300'
                  : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
              }`}
              title="Show or hide game rules modal on the big screen"
            >
              <SafeIcon icon={FiBookOpen} />
              <span>{activeSportEvent?.showRulesOnProjector ? 'Rules Showing on Stage' : 'Show Rules on Stage'}</span>
            </button>

            {activeSportEvent && (
              <button
                type="button"
                onClick={handleClearBroadcast}
                className="px-3 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/40 text-red-300 text-xs font-bold transition-colors"
                title="Stop broadcasting this event"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 4 DYNAMIC VERTICAL HOUSE BAROMETER GAUGE COLUMNS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {sortedHouses.map((house, rankIndex) => {
          if (!house) return null;
          const score = safeScores[house.id] || 0;
          const medals = safeMedals[house.id] || { gold: 0, silver: 0, bronze: 0 };
          const fillPercent = Math.min(100, Math.round((score / maxScore) * 100));
          const isLeader = rankIndex === 0 && score > 0;

          const colorStyles = {
            red: { border: 'border-red-400 dark:border-red-600', fill: 'from-red-500 to-rose-600', text: 'text-red-500' },
            blue: { border: 'border-blue-400 dark:border-blue-600', fill: 'from-blue-500 to-indigo-600', text: 'text-blue-500' },
            green: { border: 'border-emerald-400 dark:border-emerald-600', fill: 'from-emerald-500 to-teal-600', text: 'text-emerald-500' },
            yellow: { border: 'border-amber-400 dark:border-amber-500', fill: 'from-amber-400 to-yellow-500', text: 'text-amber-500' }
          }[house.id] || { border: 'border-indigo-400', fill: 'from-indigo-500 to-purple-600', text: 'text-indigo-500' };

          return (
            <motion.div
              key={house.id}
              layout
              className={`p-5 rounded-2xl bg-white dark:bg-slate-900 border-2 ${isLeader ? 'border-amber-400 shadow-xl shadow-amber-500/10' : colorStyles.border} flex flex-col justify-between relative overflow-hidden transition-all`}
            >
              {isLeader && (
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-amber-400 text-slate-950 text-[10px] font-black uppercase tracking-wider flex items-center gap-1 shadow-sm">
                  <span>🏆 #1 LEADER</span>
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl p-2 rounded-xl bg-slate-100 dark:bg-slate-800">{house.badge}</span>
                <div>
                  <h3 className="text-sm font-black text-slate-900 dark:text-white leading-tight">
                    {house.name}
                  </h3>
                  <span className="text-xs font-bold text-slate-400">{house.house}</span>
                </div>
              </div>

              {/* Barometer Liquid Tube (Thermometer) */}
              <div className="space-y-2 mb-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs font-bold text-slate-500">Championship Points</span>
                  <span className={`text-2xl font-black font-mono ${colorStyles.text}`}>
                    {score}
                  </span>
                </div>

                <div className="h-6 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden p-0.5 border border-slate-200 dark:border-slate-700 relative">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.max(5, fillPercent)}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className={`h-full rounded-full bg-gradient-to-r ${colorStyles.fill} relative overflow-hidden`}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                  </motion.div>
                </div>
                <div className="flex justify-between text-[10px] text-slate-400 font-bold">
                  <span>Rank #{rankIndex + 1}</span>
                  <span>{fillPercent}% Barometer Level</span>
                </div>
              </div>

              {/* Medals Showcase Shelf */}
              <div className="grid grid-cols-3 gap-1.5 p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 mb-4 text-center">
                <div className="py-1">
                  <span className="text-xs block">🥇 Gold</span>
                  <strong className="text-sm font-black text-amber-500 font-mono">{medals.gold}</strong>
                </div>
                <div className="py-1 border-x border-slate-200 dark:border-slate-700">
                  <span className="text-xs block">🥈 Silver</span>
                  <strong className="text-sm font-black text-slate-400 font-mono">{medals.silver}</strong>
                </div>
                <div className="py-1">
                  <span className="text-xs block">🥉 Bronze</span>
                  <strong className="text-sm font-black text-amber-700 font-mono">{medals.bronze}</strong>
                </div>
              </div>

              {/* Instant Score Bumpers */}
              <div className="grid grid-cols-4 gap-1 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => handleScoreBump(house.id, 10)}
                  className="py-1.5 bg-emerald-500/10 hover:bg-emerald-500 text-emerald-600 hover:text-white text-xs font-bold rounded-lg border border-emerald-500/30 transition-all text-center"
                  title="Add 10 points"
                >
                  +10
                </button>
                <button
                  type="button"
                  onClick={() => handleScoreBump(house.id, 25)}
                  className="py-1.5 bg-indigo-500/10 hover:bg-indigo-500 text-indigo-600 hover:text-white text-xs font-bold rounded-lg border border-indigo-500/30 transition-all text-center"
                  title="Add 25 points"
                >
                  +25
                </button>
                <button
                  type="button"
                  onClick={() => handleScoreBump(house.id, 50)}
                  className="py-1.5 bg-purple-500/10 hover:bg-purple-500 text-purple-600 hover:text-white text-xs font-bold rounded-lg border border-purple-500/30 transition-all text-center"
                  title="Add 50 points"
                >
                  +50
                </button>
                <button
                  type="button"
                  onClick={() => handleScoreBump(house.id, -10)}
                  className="py-1.5 bg-red-500/10 hover:bg-red-500 text-red-600 hover:text-white text-xs font-bold rounded-lg border border-red-500/30 transition-all text-center"
                  title="Deduct 10 points"
                >
                  -10
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* PHYSICAL SPORTS & UNDER-7 INDOOR GAMES SCORING CONSOLE */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-5">
        
        {/* Header with Custom Sport Creator button */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
          <div>
            <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
              <SafeIcon icon={FiActivity} className="text-indigo-500" />
              Award Physical Sports, Indoor Games & Athletic Matchups
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Select any default or custom game. You can broadcast it live on the projector view, showcase rules to kids, and award points.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsCreateSportOpen(true)}
              className="px-3 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition-colors"
            >
              <SafeIcon icon={FiPlus} />
              <span>+ Add Custom Sport to Library</span>
            </button>

            <button
              type="button"
              onClick={handleResetAll}
              className="text-xs font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 border border-red-200 dark:border-red-800 px-3 py-1.5 rounded-xl transition-colors"
            >
              Reset Scores & History
            </button>
          </div>
        </div>

        {/* Category Filter Pills for Quick Navigation */}
        <div className="space-y-1.5">
          <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">
            Filter Sports & Games By Category:
          </label>
          <div className="flex flex-wrap items-center gap-1.5">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => {
                  setSelectedCategory(cat);
                  const firstInCat = allSportsList.find(s => cat === 'All Games' ? true : s.category === cat);
                  if (firstInCat) setSelectedSport(firstInCat.name);
                }}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  selectedCategory === cat
                    ? 'bg-indigo-600 text-white shadow-sm ring-2 ring-indigo-400'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                }`}
              >
                {cat === 'Indoor Fun (<7 Years)' ? '🎈 Indoor Fun (<7 Years)' : cat}
              </button>
            ))}
          </div>
        </div>

        {/* The Award Form */}
        <form onSubmit={handleAwardSport} className="space-y-4 pt-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* 1. Sport Name Dropdown */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase">
                  Sport / Game Name
                </label>
                {currentSportObject && (
                  <button
                    type="button"
                    onClick={() => {
                      setRulesSportData(currentSportObject);
                      setIsRulesModalOpen(true);
                    }}
                    className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                  >
                    <SafeIcon icon={FiBookOpen} />
                    <span>View Rules</span>
                  </button>
                )}
              </div>
              <select
                value={selectedSport}
                onChange={e => {
                  setSelectedSport(e.target.value);
                  setCustomSportInput('');
                }}
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2.5 text-xs text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
              >
                {filteredSportsList.map(sp => (
                  <option key={sp.id} value={sp.name}>
                    {sp.icon} {sp.name} ({sp.category})
                  </option>
                ))}
              </select>
            </div>

            {/* 2. Custom Sport Name Override (Optional) */}
            <div>
              <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                Or Type Custom Sport Name
              </label>
              <input
                type="text"
                value={customSportInput}
                onChange={e => setCustomSportInput(e.target.value)}
                placeholder="e.g. 50m Swimming, Musical Chairs, Kabaddi..."
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {customSportInput.trim() && (
                <label className="flex items-center gap-1.5 mt-1 text-[11px] text-slate-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveCustomToLibrary}
                    onChange={e => setSaveCustomToLibrary(e.target.checked)}
                    className="rounded text-indigo-600"
                  />
                  <span>Save to library for future matches & projector</span>
                </label>
              )}
            </div>

            {/* 3. Division / Age Grade */}
            <div>
              <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                Age Division / Grade
              </label>
              <select
                value={selectedDivision}
                onChange={e => setSelectedDivision(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2.5 text-xs text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Under 7 (Ages 3-6)">🧒 Under 7 (Ages 3-6 / Little Champs)</option>
                <option value="Sub-Junior (Grades 1-4)">Sub-Junior (Grades 1-4)</option>
                <option value="Junior (Grades 5-8)">Junior (Grades 5-8)</option>
                <option value="Senior (Grades 9-12)">Senior (Grades 9-12)</option>
                <option value="Open All-Stars">Open All-Stars (All Ages)</option>
                <option value="Boys Championship">Boys Championship</option>
                <option value="Girls Championship">Girls Championship</option>
              </select>
            </div>

            {/* 4. Position Award */}
            <div>
              <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                Position & Award
              </label>
              <div className="grid grid-cols-4 gap-1">
                <button
                  type="button"
                  onClick={() => handlePlaceChange('1st')}
                  className={`py-2 rounded-xl text-xs font-black transition-all ${
                    selectedPlace === '1st'
                      ? 'bg-amber-400 text-slate-950 shadow-md ring-2 ring-amber-500'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  🥇 1st
                </button>
                <button
                  type="button"
                  onClick={() => handlePlaceChange('2nd')}
                  className={`py-2 rounded-xl text-xs font-black transition-all ${
                    selectedPlace === '2nd'
                      ? 'bg-slate-300 text-slate-900 shadow-md ring-2 ring-slate-400'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  🥈 2nd
                </button>
                <button
                  type="button"
                  onClick={() => handlePlaceChange('3rd')}
                  className={`py-2 rounded-xl text-xs font-black transition-all ${
                    selectedPlace === '3rd'
                      ? 'bg-amber-700 text-white shadow-md ring-2 ring-amber-800'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  🥉 3rd
                </button>
                <button
                  type="button"
                  onClick={() => handlePlaceChange('participation')}
                  className={`py-2 rounded-xl text-[10px] font-bold transition-all ${
                    selectedPlace === 'participation'
                      ? 'bg-indigo-600 text-white shadow-md ring-2 ring-indigo-500'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  🎖️ Part.
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 5. Awarded House */}
            <div>
              <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                Awarded House
              </label>
              <select
                value={selectedHouse}
                onChange={e => setSelectedHouse(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2.5 text-xs text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 font-bold"
              >
                {houses.map(h => (
                  <option key={h.id} value={h.id}>
                    {h.badge} {h.name} ({h.house})
                  </option>
                ))}
              </select>
            </div>

            {/* 6. Points Awarded */}
            <div>
              <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                Points to Add
              </label>
              <input
                type="number"
                min="1"
                max="500"
                value={pointsInput}
                onChange={e => setPointsInput(Number(e.target.value))}
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-800 dark:text-white font-mono font-bold outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* 7. Winning Student / Team */}
            <div>
              <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                Winning Student / Athlete Name
              </label>
              <input
                type="text"
                value={studentName}
                onChange={e => setStudentName(e.target.value)}
                placeholder="e.g. Aarav Sharma or Relay Team A"
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* 8. Submit Button */}
            <div className="flex items-end">
              <button
                type="submit"
                className="w-full py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <SafeIcon icon={FiAward} />
                <span>Award {pointsInput} Pts to House</span>
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* MATCH HISTORY & AUDIT LOG */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-black text-slate-900 dark:text-white flex items-center gap-2">
              <SafeIcon icon={FiAward} className="text-amber-500" />
              Sports Day Log & Match History ({sportsEvents.length} Matches Logged)
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Complete record of physical events. Deleting any match automatically rolls back points and medals.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={filterHouse}
              onChange={e => setFilterHouse(e.target.value)}
              className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 dark:text-slate-300 outline-none"
            >
              <option value="all">All Houses</option>
              {houses.map(h => (
                <option key={h.id} value={h.id}>{h.badge} {h.name}</option>
              ))}
            </select>

            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search athlete or sport..."
                className="pl-7 pr-3 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-800 dark:text-white outline-none"
              />
            </div>
          </div>
        </div>

        <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
          {filteredEvents.length === 0 ? (
            <div className="p-8 text-center text-slate-400 space-y-2">
              <span className="text-3xl block">🏆</span>
              <p className="text-sm font-bold">No physical sports events recorded yet.</p>
              <p className="text-xs">Use the console above to award 100m sprint, Under-7 balloon keep-up, sack races, or custom matches!</p>
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 font-bold uppercase sticky top-0 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-2.5">#</th>
                  <th className="px-4 py-2.5">Sport / Event</th>
                  <th className="px-4 py-2.5">House</th>
                  <th className="px-4 py-2.5">Student / Athlete</th>
                  <th className="px-4 py-2.5">Place</th>
                  <th className="px-4 py-2.5">Points</th>
                  <th className="px-4 py-2.5">Time</th>
                  <th className="px-4 py-2.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-sans">
                {filteredEvents.map((ev, index) => {
                  const medalBadge = ev.place === '1st' ? '🥇 Gold' : ev.place === '2nd' ? '🥈 Silver' : ev.place === '3rd' ? '🥉 Bronze' : '🎖️ Part.';
                  return (
                    <tr key={ev.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="px-4 py-2.5 text-slate-400">{index + 1}</td>
                      <td className="px-4 py-2.5 font-bold text-slate-800 dark:text-slate-200">
                        {ev.sportName}
                        <span className="block text-[10px] text-slate-400 font-normal">{ev.division}</span>
                      </td>
                      <td className="px-4 py-2.5 font-bold">
                        <span className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                          {ev.houseName}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">
                        {ev.studentName}
                      </td>
                      <td className="px-4 py-2.5 font-bold">
                        {medalBadge}
                      </td>
                      <td className="px-4 py-2.5 font-mono font-bold text-emerald-600 dark:text-emerald-400">
                        +{ev.points} pts
                      </td>
                      <td className="px-4 py-2.5 text-[10px] text-slate-400">
                        {new Date(ev.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => handleDeleteEvent(ev)}
                          className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                          title="Rollback this event"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* GAME RULES PREVIEW MODAL */}
      <AnimatePresence>
        {isRulesModalOpen && rulesSportData && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 max-w-xl w-full shadow-2xl relative space-y-4"
            >
              <div className="flex items-start justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                <div className="flex items-center gap-3">
                  <span className="text-3xl p-2 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50">
                    {rulesSportData.icon || '🏆'}
                  </span>
                  <div>
                    <span className="text-[10px] font-black uppercase text-indigo-600 dark:text-indigo-400 tracking-wider">
                      {rulesSportData.category || 'Physical Sport & Games'}
                    </span>
                    <h3 className="text-lg font-black text-slate-900 dark:text-white">
                      {rulesSportData.name}
                    </h3>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsRulesModalOpen(false)}
                  className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400"
                >
                  <SafeIcon icon={FiX} className="text-lg" />
                </button>
              </div>

              {/* Recommended Age & Objective */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="font-bold uppercase">Target Age:</span>
                  <span className="px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-semibold">
                    {rulesSportData.recommendedAge || 'All Divisions'}
                  </span>
                </div>
                {rulesSportData.objective && (
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-xs">
                    <strong className="text-slate-800 dark:text-white block mb-0.5">Objective:</strong>
                    <span className="text-slate-600 dark:text-slate-300">{rulesSportData.objective}</span>
                  </div>
                )}
              </div>

              {/* Rules List */}
              <div className="space-y-2">
                <h4 className="text-xs font-black uppercase text-slate-700 dark:text-slate-300">
                  Official Match Rules & Guidelines:
                </h4>
                <ul className="space-y-1.5 text-xs text-slate-600 dark:text-slate-300">
                  {(rulesSportData.rules || []).map((rule, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">
                        {idx + 1}
                      </span>
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Footer Controls */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    handleBroadcastEvent(rulesSportData, true);
                    setIsRulesModalOpen(false);
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 shadow-md"
                >
                  <SafeIcon icon={FiTv} />
                  <span>Broadcast & Show Rules on Projector</span>
                </button>

                <button
                  type="button"
                  onClick={() => setIsRulesModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* CREATE CUSTOM SPORT MODAL */}
      <AnimatePresence>
        {isCreateSportOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative space-y-4"
            >
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <SafeIcon icon={FiPlus} className="text-purple-600" />
                  Add New Custom Sport to Library
                </h3>
                <button
                  type="button"
                  onClick={() => setIsCreateSportOpen(false)}
                  className="p-1 rounded-full text-slate-400 hover:text-slate-600"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              <form onSubmit={handleCreateNewSport} className="space-y-3.5 text-xs">
                <div>
                  <label className="block font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                    Sport / Game Name
                  </label>
                  <input
                    type="text"
                    required
                    value={newSportForm.name}
                    onChange={e => setNewSportForm({ ...newSportForm, name: e.target.value })}
                    placeholder="e.g. 50m Butterfly Swimming or Kabaddi Final"
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-purple-500 font-semibold"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                      Category
                    </label>
                    <select
                      value={newSportForm.category}
                      onChange={e => setNewSportForm({ ...newSportForm, category: e.target.value })}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-800 dark:text-white outline-none"
                    >
                      <option value="Indoor Fun (<7 Years)">{"🎈 Indoor Fun (<7 Years)"}</option>
                      <option value="Track & Athletics">🏃 Track & Athletics</option>
                      <option value="Field & Team Games">🤼 Field & Team Games</option>
                      <option value="Fun & Carnival Races">🥔 Fun & Carnival Races</option>
                      <option value="Drill & Team Spirit">🎖️ Drill & Team Spirit</option>
                      <option value="Custom Event">⭐ Custom Event</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                      Target Age / Division
                    </label>
                    <input
                      type="text"
                      value={newSportForm.recommendedAge}
                      onChange={e => setNewSportForm({ ...newSportForm, recommendedAge: e.target.value })}
                      placeholder="e.g. Under 7 or Grades 5-8"
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-800 dark:text-white outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                    Game Objective
                  </label>
                  <input
                    type="text"
                    value={newSportForm.objective}
                    onChange={e => setNewSportForm({ ...newSportForm, objective: e.target.value })}
                    placeholder="e.g. Complete the course or score the highest points"
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-800 dark:text-white outline-none"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                    Game Rules (One rule per line)
                  </label>
                  <textarea
                    rows={4}
                    value={newSportForm.rules}
                    onChange={e => setNewSportForm({ ...newSportForm, rules: e.target.value })}
                    placeholder="Rule 1: Teams line up at the starting line...&#10;Rule 2: Whistle signals start of race...&#10;Rule 3: First to tag wins."
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl p-3 text-slate-800 dark:text-white outline-none whitespace-pre"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => setIsCreateSportOpen(false)}
                    className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl font-bold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold shadow-md"
                  >
                    Save & Broadcast Custom Sport
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}

class BarometerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("SchoolSportsBarometerManager Error:", error, info);
  }
  handleReset = () => {
    try {
      localStorage.removeItem('gamifycenter_sports_events');
      localStorage.removeItem('gamifycenter_house_scores');
      localStorage.removeItem('gamifycenter_house_medals');
    } catch (e) {}
    window.location.reload();
  };
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center bg-white dark:bg-slate-900 border border-amber-300 dark:border-amber-800 rounded-2xl space-y-4">
          <span className="text-4xl block">🏆</span>
          <h3 className="text-lg font-black text-slate-800 dark:text-white">
            School House Championship Barometer
          </h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            The scoring manager encountered a state refresh event. Click below to reload.
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-xl text-xs"
            >
              Reload Section
            </button>
            <button
              onClick={this.handleReset}
              className="px-4 py-2 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold rounded-xl text-xs"
            >
              Reset Storage & Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function SchoolSportsBarometerManager() {
  return (
    <BarometerErrorBoundary>
      <SchoolSportsBarometerManagerComponent />
    </BarometerErrorBoundary>
  );
}
